/**
 * SSPS Card Inline Cart & Quantity Stepper Controller
 * Provides seamless, instant inline Add-to-Cart and +/- quantity stepper directly on product cards
 * No popups, no page reload, instant optimistic UI updates, and automated cart synchronization
 */
(function () {
  'use strict';

  let cartData = null;
  let isFetchingCart = false;
  const pendingRequests = new Map(); // variantId -> timeoutId

  // Helper: Update all cart badges across header, mobile navigation bar, and drawers
  function updateCartBadges(count) {
    const badges = document.querySelectorAll(
      '#cart-notification-count, #cart-notification-count-mobile, .header__actions_btn_cart_num, .mobile__nav-badge, .cart-count-bubble'
    );
    const numCount = Number(count) || 0;
    badges.forEach((badge) => {
      badge.textContent = numCount;
      if (numCount > 0) {
        badge.style.display = '';
        badge.classList.remove('d-none');
      } else {
        badge.style.display = 'none';
        badge.classList.add('d-none');
      }
    });
  }

  // Helper: Fetch latest cart data from Shopify Cart API
  async function refreshCart() {
    if (isFetchingCart) return;
    isFetchingCart = true;
    try {
      const res = await fetch('/cart.js', {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      if (!res.ok) throw new Error('Failed to fetch /cart.js');
      cartData = await res.json();
      updateCartBadges(cartData.item_count);
      syncAllCards();
    } catch (err) {
      console.warn('[SSPS Cart] Error fetching cart:', err);
    } finally {
      isFetchingCart = false;
    }
  }

  // Helper: Sync all product cards on the current page with current cart items
  function syncAllCards() {
    if (!cartData || !cartData.items) return;

    const variantQtyMap = {};
    cartData.items.forEach((item) => {
      variantQtyMap[item.id] = (variantQtyMap[item.id] || 0) + item.quantity;
      if (item.variant_id) {
        variantQtyMap[item.variant_id] = (variantQtyMap[item.variant_id] || 0) + item.quantity;
      }
    });

    const controls = document.querySelectorAll('.ssps-card-cart-control');
    controls.forEach((control) => {
      const variantId = control.dataset.variantId;
      if (!variantId) return;
      const qty = variantQtyMap[variantId] || 0;
      updateControlUI(control, qty);
    });
  }

  // Helper: Update a single control's DOM to show either "Add to Cart" or the Stepper
  function updateControlUI(control, qty) {
    const countEl = control.querySelector('.ssps-qty-count');

    if (qty > 0) {
      control.classList.add('is-active');
      if (countEl) countEl.textContent = qty;
    } else {
      control.classList.remove('is-active');
      if (countEl) countEl.textContent = '1';
    }
  }

  // Helper: Submit quantity changes to Shopify cart API with debouncing
  function updateCartQuantity(variantId, newQty) {
    // 1. Optimistic UI update on all cards displaying this variant
    const matchingControls = document.querySelectorAll(`.ssps-card-cart-control[data-variant-id="${variantId}"]`);
    matchingControls.forEach((c) => updateControlUI(c, newQty));

    // 2. Optimistic update on cart badge count
    if (cartData && cartData.items) {
      const oldQty = cartData.items.reduce((sum, item) => {
        return (item.id == variantId || item.variant_id == variantId) ? sum + item.quantity : sum;
      }, 0);
      const diff = newQty - oldQty;
      const optimisticTotal = Math.max(0, (cartData.item_count || 0) + diff);
      updateCartBadges(optimisticTotal);
    }

    // 3. Clear any existing timer for this variant
    if (pendingRequests.has(variantId)) {
      clearTimeout(pendingRequests.get(variantId));
    }

    // 4. Send request with a lightweight debounce (120ms) for ultra-fast, smooth responsiveness
    const timer = setTimeout(async () => {
      pendingRequests.delete(variantId);
      try {
        const changeUrl = window.routes?.cart_change_url || '/cart/change.js';
        const res = await fetch(changeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            id: String(variantId),
            quantity: newQty,
            sections: 'mini-cart-drawer,cart-notification-count'
          })
        });

        if (!res.ok) throw new Error('Cart update failed');

        const updatedCart = await res.json();
        cartData = updatedCart;
        updateCartBadges(updatedCart.item_count);
        syncAllCards();

        // Silently update mini cart drawer HTML if returned
        if (updatedCart.sections && updatedCart.sections['mini-cart-drawer']) {
          const drawer = document.getElementById('mini-cart-drawer');
          if (drawer) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(updatedCart.sections['mini-cart-drawer'], 'text/html');
            const newContent = doc.getElementById('mini-cart-drawer');
            if (newContent) {
              drawer.innerHTML = newContent.innerHTML;
            }
          }
        }

        // Notify other theme components
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: updatedCart } }));
      } catch (err) {
        console.error('[SSPS Cart] Error updating cart quantity:', err);
        // Rollback to server state
        refreshCart();
      }
    }, 120);

    pendingRequests.set(variantId, timer);
  }

  // Global click event delegation using capture phase to intercept button clicks before card links
  document.addEventListener('click', function (e) {
    const target = e.target;
    const addBtn = target.closest('.ssps-card-add-btn');
    const plusBtn = target.closest('.ssps-qty-btn--plus');
    const minusBtn = target.closest('.ssps-qty-btn--minus');

    if (!addBtn && !plusBtn && !minusBtn) return;

    // Stop event propagation completely so no modal, drawer, or page navigation opens!
    e.preventDefault();
    e.stopPropagation();

    const control = target.closest('.ssps-card-cart-control');
    if (!control) return;

    const variantId = control.dataset.variantId;
    if (!variantId) return;

    const countEl = control.querySelector('.ssps-qty-count');
    const currentQty = countEl ? (parseInt(countEl.textContent.trim(), 10) || 0) : 0;

    if (addBtn) {
      updateCartQuantity(variantId, 1);
    } else if (plusBtn) {
      updateCartQuantity(variantId, currentQty + 1);
    } else if (minusBtn) {
      updateCartQuantity(variantId, Math.max(0, currentQty - 1));
    }
  }, true);

  // Initialize on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshCart);
  } else {
    refreshCart();
  }

  // Observe dynamically loaded items (infinite scroll, pagination, tab switching)
  const observer = new MutationObserver(function (mutations) {
    let shouldSync = false;
    for (let mutation of mutations) {
      if (mutation.addedNodes.length > 0) {
        for (let node of mutation.addedNodes) {
          if (node.nodeType === 1 && (node.matches?.('.ssps-card-cart-control, .product__card, .grid__item') || node.querySelector?.('.ssps-card-cart-control'))) {
            shouldSync = true;
            break;
          }
        }
      }
      if (shouldSync) break;
    }
    if (shouldSync) {
      syncAllCards();
    }
  });

  observer.observe(document.body || document.documentElement, {
    childList: true,
    subtree: true
  });

  // Expose helper on window.theme
  window.theme = window.theme || {};
  window.theme.refreshCardInlineCart = refreshCart;
})();
